import React, { useEffect, useState, useCallback } from 'react';
import { Table, Pagination, Form, Row, Col, Button, Card, Spinner, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { formatDate } from '../../utils/dateFormatter';
import { useConfigBasedTimeFilter, getStatusText, getToggleButtonText } from '../../utils/timeFilter';
import { validateCsvFile, validateUpdateCsvFile } from '../../utils/csvUtils';
import { useSubscribers } from '../../services/api/hooks/useSubscribers';
import { useTags } from '../../services/api/hooks/useTags';
import { useTopics } from '../../services/api/hooks/useTopics';
import { apiClient } from '../../services/api';
import { REASON_LABELS } from '../../utils/unsubscribeReasons';

const Subscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [filters, setFilters] = useState({ email: '', source: [], tags: [], negativeTags: [], optIn: 'true', startDate: '', endDate: '', countries: [], lastOpenedStartDate: '', lastOpenedEndDate: '', topic_id: undefined as number | undefined });
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [sourceOptions, setSourceOptions] = useState<any[]>([]);
  const [campaignTagToAssign, setCampaignTagToAssign] = useState('');
  const [campaignTagsToAssign, setCampaignTagsToAssign] = useState<string[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isAssigningTag, setIsAssigningTag] = useState(false);
  const [isAssigningMultipleTags, setIsAssigningMultipleTags] = useState(false);
  const [campaignTagToRemove, setCampaignTagToRemove] = useState('');
  const [isRemovingTag, setIsRemovingTag] = useState(false);
  const [campaignTagsToRemove, setCampaignTagsToRemove] = useState<string[]>([]);
  const [isRemovingMultipleTags, setIsRemovingMultipleTags] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [isFetchingSummary, setIsFetchingSummary] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const [skipDebounce, setSkipDebounce] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOptingOut, setIsOptingOut] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasFiltered, setHasFiltered] = useState(false);
  const [showModifyCsv, setShowModifyCsv] = useState(false);
  const [modifyCsvFile, setModifyCsvFile] = useState<File | null>(null);
  const [isModifying, setIsModifying] = useState(false);
  const [modifyResult, setModifyResult] = useState<string | null>(null);
  const { showAll: showAllTags, setShowAll: setShowAllTags, timeFilter } = useConfigBasedTimeFilter();

  const { data: topics } = useTopics();
  
  // Get default topic (always required)
  const defaultTopic = topics?.find((t: any) => t.is_default);
  const defaultTopicId = defaultTopic?.id || topics?.[0]?.id;

  const { 
    getSources, 
    getCountries, 
    assignTagsToSubscribers, 
    removeTagsFromSubscribers, 
    downloadSampleCsv, 
    uploadCsv, 
    deleteFiltered, 
    refreshTagCounts,
    optoutFiltered,
    downloadCsv,
    getFilteredSubscribers,
    getSubscriberSummary,
    assignMultipleTagsToSubscribers,
    removeMultipleTagsFromSubscribers,
    updateCsv
  } = useSubscribers();
  
  const { data: allTags } = useTags({ type: 'recent', showAll: showAllTags });
  const { data: assignableTags } = useTags({ type: 'assignable' });
  const tagOptions = allTags.map(tag => ({ value: tag.name, label: tag.name }));
  const assignableTagOptions = assignableTags.map(tag => ({ value: tag.name, label: tag.name }));
  const topicOptions = topics.map(topic => ({ value: topic.id.toString(), label: topic.name }));
  const getEffectiveFilters = useCallback(() => (
    filters.topic_id || defaultTopicId
      ? { ...filters, topic_id: filters.topic_id || defaultTopicId }
      : filters
  ), [filters, defaultTopicId]);

  useEffect(() => {
    getSources().then(data => {
      const options = data.map((source: any) => ({ 
        value: source.name, 
        label: `${source.name} (${source.subscriberCount})` 
      }));
      setSourceOptions(options);
    });

    getCountries().then(data => {
      const options = data.map((country: any) => ({ value: country, label: country })).sort((a: any, b: any) => a.label.localeCompare(b.label));
      setCountryOptions(options);
    });
  }, [getSources, getCountries]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    // @ts-ignore
    const checked = e.target.checked;

    if (name === 'topic_id') {
      setFilters({ ...filters, topic_id: value ? parseInt(value) : undefined });
    } else {
      setFilters({ ...filters, [name]: isCheckbox ? (checked ? 'true' : 'false') : value });
    }
  };

  const handleTagChange = (selectedOptions: any) => {
    setFilters({ ...filters, tags: selectedOptions ? selectedOptions.map((option: any) => option.value) : [] });
  };

  const handleNegativeTagChange = (selectedOptions: any) => {
    setFilters({ ...filters, negativeTags: selectedOptions ? selectedOptions.map((option: any) => option.value) : [] });
  };

  const handleSourceChange = (selectedOptions: any) => {
    setFilters({ ...filters, source: selectedOptions ? selectedOptions.map((option: any) => option.value) : [] });
  };

  

  const handleCountryChange = (selectedOptions: any) => {
    setFilters({ ...filters, countries: selectedOptions ? selectedOptions.map((option: any) => option.value) : [] });
  };



  const handleSummaryClick = useCallback(async () => {
    setIsFetchingSummary(true);
    setShowSummary(false);
    try {
      const data = await getSubscriberSummary(getEffectiveFilters());
      setSummaryData(data);
      setShowSummary(true);
    } catch (error) {
      console.error('Error fetching summary:', error);
      alert('Failed to fetch summary data.');
    } finally {
      setIsFetchingSummary(false);
    }
  }, [getEffectiveFilters, getSubscriberSummary]);

  const applyFilters = useCallback(async () => {
    if (filters.email && filters.email.length < 3) {
      alert('Email filter must be at least 3 characters long.');
      return;
    }

    setIsFiltering(true);
    setPage(1);
    setShowDetails(false);
    setSubscribers([]);
    try {
      const effectiveFilters = getEffectiveFilters();
      const params: Record<string, string> = {};
      
      if (effectiveFilters.email) params.email = effectiveFilters.email;
      if (effectiveFilters.source?.length > 0) params.source = effectiveFilters.source.join(',');
      if (effectiveFilters.tags?.length > 0) params.tags = effectiveFilters.tags.join(',');
      if (effectiveFilters.negativeTags?.length > 0) params.negativeTags = effectiveFilters.negativeTags.join(',');
      if (effectiveFilters.countries?.length > 0) params.countries = effectiveFilters.countries.join(',');
      if (effectiveFilters.optIn === 'true') params.optIn = 'true';
      if (effectiveFilters.optIn === 'false') params.optIn = 'false';
      if (effectiveFilters.startDate) params.startDate = effectiveFilters.startDate;
      if (effectiveFilters.endDate) params.endDate = effectiveFilters.endDate;
      if (effectiveFilters.lastOpenedStartDate) params.lastOpenedStartDate = effectiveFilters.lastOpenedStartDate;
      if (effectiveFilters.lastOpenedEndDate) params.lastOpenedEndDate = effectiveFilters.lastOpenedEndDate;
      if (effectiveFilters.topic_id) params.topic_id = effectiveFilters.topic_id.toString();
      
      const data = await apiClient.get('/api/subscribers/count', params) as any;
      const totalCount = data.totalSubscribers || 0;
      setTotalSubscribers(totalCount);
      setTotalPages(Math.ceil(totalCount / 20));
      setHasFiltered(true);
    } catch (error) {
      console.error('Error filtering subscribers:', error);
      alert('Failed to filter subscribers.');
    } finally {
      setIsFiltering(false);
    }
  }, [filters, getEffectiveFilters]);

  const loadDetails = useCallback(async () => {
    setIsFiltering(true);
    try {
      const data = await getFilteredSubscribers(getEffectiveFilters(), 1) as any;
      setSubscribers(data.subscribers);
      setShowDetails(true);
      setPage(1);
    } catch (error) {
      console.error('Error loading subscriber details:', error);
      alert('Failed to load subscriber details.');
    } finally {
      setIsFiltering(false);
    }
  }, [getEffectiveFilters, getFilteredSubscribers]);

  const handlePageChange = useCallback(async (newPage: number) => {
    if (!showDetails || totalSubscribers === 0) return;
    
    setIsFiltering(true);
    try {
      const data = await getFilteredSubscribers(getEffectiveFilters(), newPage) as any;
      setSubscribers(data.subscribers);
      setPage(newPage);
    } catch (error) {
      console.error('Error loading page:', error);
      alert('Failed to load page.');
    } finally {
      setIsFiltering(false);
    }
  }, [getEffectiveFilters, getFilteredSubscribers, showDetails, totalSubscribers]);

  const handleAssignToCampaign = async () => {
    if (!campaignTagToAssign) {
      alert('Please enter a tag to assign.');
      return;
    }

    let confirmMessage = `This will start a background process to assign the tag "${campaignTagToAssign}" to all subscribers matching the current filters. Are you sure you want to continue?`;
    
    if (filters.optIn === 'false') {
      confirmMessage = `WARNING: You are about to assign tags to NON-OPTED-IN subscribers. This may violate email marketing regulations.

${confirmMessage}`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsAssigningTag(true);
    try {
      const result = await assignTagsToSubscribers(getEffectiveFilters(), campaignTagToAssign) as any;
      alert(`${result.message} (Workflow ID: ${result.workflowId})`);
      setCampaignTagToAssign('');
    } catch (error) {
      console.error('Error assigning tag to subscribers:', error);
      alert('Failed to assign tag to subscribers.');
    } finally {
      setIsAssigningTag(false);
    }
  };

  const handleRemoveTagFromCampaign = async () => {
    if (!campaignTagToRemove) {
      alert('Please enter a tag to remove.');
      return;
    }

    if (!window.confirm(`This will start a background process to remove the tag "${campaignTagToRemove}" from all subscribers matching the current filters. Are you sure you want to continue?`)) {
      return;
    }

    setIsRemovingTag(true);
    try {
      const result = await removeTagsFromSubscribers(getEffectiveFilters(), campaignTagToRemove) as any;
      alert(`${result.message} (Workflow ID: ${result.workflowId})`);
      setCampaignTagToRemove('');
    } catch (error) {
      console.error('Error removing tag from subscribers:', error);
      alert('Failed to remove tag from subscribers.');
    } finally {
      setIsRemovingTag(false);
    }
  };

  const handleRemoveMultipleTagsFromCampaign = async () => {
    if (campaignTagsToRemove.length === 0) {
      alert('Please select at least one tag to remove.');
      return;
    }

    if (!window.confirm(`This will start a background process to remove the tags "${campaignTagsToRemove.join(', ')}" from all subscribers matching the current filters. Are you sure you want to continue?`)) {
      return;
    }

    setIsRemovingMultipleTags(true);
    try {
      const result = await removeMultipleTagsFromSubscribers(getEffectiveFilters(), campaignTagsToRemove) as any;
      alert(`${result.message}\n\nTotal workflows created: ${result.workflowIds?.length || 0}`);
      setCampaignTagsToRemove([]);
    } catch (error) {
      console.error('Error removing multiple tags from subscribers:', error);
      alert('Failed to remove multiple tags from subscribers.');
    } finally {
      setIsRemovingMultipleTags(false);
    }
  };

  const handleAssignMultipleTagsToCampaign = async () => {
    if (campaignTagsToAssign.length === 0) {
      alert('Please select at least one tag to assign.');
      return;
    }

    let confirmMessage = `This will start a background process to assign the tags "${campaignTagsToAssign.join(', ')}" to all subscribers matching the current filters. Are you sure you want to continue?`;
    
    if (filters.optIn === 'false') {
      confirmMessage = `WARNING: You are about to assign tags to NON-OPTED-IN subscribers. This may violate email marketing regulations.${confirmMessage}`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsAssigningMultipleTags(true);
    try {
      const result = await assignMultipleTagsToSubscribers(getEffectiveFilters(), campaignTagsToAssign) as any;
      const detailedMessage = `${result.message}\n\nDetails:\n• Each tag assigned to ${result.subscribersPerTag.toLocaleString()} subscribers\n• Total workflows created: ${result.workflowIds?.length || 0}`;
      alert(detailedMessage);
      setCampaignTagsToAssign([]);
    } catch (error) {
      console.error('Error assigning multiple tags to subscribers:', error);
      alert('Failed to assign multiple tags to subscribers.');
    } finally {
      setIsAssigningMultipleTags(false);
    }
  };


  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCsvFile(file);
    setUploadResult(null);
  };

  const handleDownloadSample = async () => {
    try {
      await downloadSampleCsv();
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      alert('Error downloading sample CSV');
    }
  };

  const handleDownloadModifySample = () => {
    const csvContent = 'email,name,phone,source,optIn,ipAddress,countryCode,stateCode,tags,topics\njohn@example.com,John Updated,+1234567890,website,true,192.168.1.1,US,CA,premium-vip,General-Updates\njane@example.com,,,api,false,,,,,';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modify_subscribers_sample.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file to upload');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);



    try {
      const validation = await validateCsvFile(csvFile);
      if (!validation.valid) {
        // Format error message similar to server responses
        let message = `Skipped: ${validation.errors.length} rows with errors\n`;
        message += `Errors (showing first ${Math.min(validation.errors.length, 10)}):`;
        validation.errors.slice(0, 10).forEach(err => {
          message += `\n- ${err}`;
        });
        if (validation.errors.length > 10) {
          message += `\n... and ${validation.errors.length - 10} more errors`;
        }
        message += `\n\nPlease fix the above errors and try uploading the CSV again.`;
        setUploadResult(message);
        setIsUploading(false);
        return;
      }

      const result = await uploadCsv(csvFile, skipDebounce) as any;
      
      let message = `Imported: ${result.totalImported.toLocaleString()} subscribers\n`;
      message += `Created: ${result.totalTagsCreated} tags, ${result.totalTagRelationships.toLocaleString()} relationships\n`;
      
      if (result.totalSkipped > 0) {
        message += `Skipped: ${result.totalSkipped} rows with errors\n`;
        if (result.errors && result.errors.length > 0) {
          message += `\nErrors (showing first ${Math.min(result.errors.length, 10)}):`;
          result.errors.slice(0, 10).forEach((err: string) => {
            message += `\n- ${err}`;
          });
          if (result.errors.length > 10) {
            message += `\n... and ${result.errors.length - 10} more errors`;
          }
        }
      }
      
      if (result.failed > 0) {
        message += `\nFailed chunks: ${result.failed}/${result.totalChunks}`;
      }
      
      if (result.duplicateEmails && result.duplicateEmails.length > 0) {
        const display = result.duplicateEmails.slice(0, 10);
        const overflow = result.duplicateEmails.length - display.length;
        message += `\nSkipped ${result.duplicateEmails.length} existing email(s) (already in database — not re-imported):\n`;
        message += display.map((e: string) => `- ${e}`).join('\n');
        if (overflow > 0) message += `\n... and ${overflow} more`;
      }
      
      setUploadResult(message);
      setCsvFile(null);
      const fileInput = document.getElementById('csvFileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      applyFilters();
    } catch (error: any) {
      setUploadResult(`Error: ${error.message || 'Upload failed'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteSubscribers = async () => {
    if (totalSubscribers === 0) {
      alert('No subscribers to delete with current filters.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${totalSubscribers} subscriber(s) matching the current filters?\n\nThis action is IRREVERSIBLE and will permanently remove the subscribers from the database.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteFiltered(getEffectiveFilters()) as any;
      alert(`Success: ${result.message}`);
      
      // Refresh tag counts after deletion
      try {
        await refreshTagCounts();
      } catch (refreshError) {
        console.error('Failed to refresh tag counts:', refreshError);
      }
      
      applyFilters();
    } catch (error: any) {
      alert(`Error: ${error.message || 'Delete operation failed'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOptOutSubscribers = async () => {
    if (totalSubscribers === 0) {
      alert('No subscribers to opt out with current filters.');
      return;
    }

    const confirmMessage = `Are you sure you want to opt out ${totalSubscribers} subscriber(s) matching the current filters?\n\nThey will be marked as unsubscribed and moved to the opted-out list.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsOptingOut(true);
    try {
      const result = await optoutFiltered(getEffectiveFilters()) as any;
      alert(`Success: ${result.message}`);
      applyFilters();
    } catch (error: any) {
      alert(`Error: ${error.message || 'Opt out operation failed'}`);
    } finally {
      setIsOptingOut(false);
    }
  };

  const handleDownloadSubscribers = async () => {
    if (totalSubscribers === 0) {
      alert('No subscribers to download with current filters.');
      return;
    }

    setIsDownloading(true);
    try {
      await downloadCsv(getEffectiveFilters());
    } catch (error) {
      console.error('Error downloading subscribers:', error);
      alert('Failed to download subscribers. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseCsvModal = () => {
    setShowCsvUpload(false);
    setCsvFile(null);
    setUploadResult(null);
    const fileInput = document.getElementById('csvFileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleModifyCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setModifyCsvFile(file);
    setModifyResult(null);
  };

  const handleModifyCsvUpload = async () => {
    if (!modifyCsvFile) {
      alert('Please select a CSV file to upload');
      return;
    }

    setIsModifying(true);
    setModifyResult(null);

    try {
      const validation = await validateUpdateCsvFile(modifyCsvFile);
      if (!validation.valid) {
        let message = `Skipped: ${validation.errors.length} rows with errors\n`;
        message += `Errors (showing first ${Math.min(validation.errors.length, 10)}):`;
        validation.errors.slice(0, 10).forEach(err => {
          message += `\n- ${err}`;
        });
        if (validation.errors.length > 10) {
          message += `\n... and ${validation.errors.length - 10} more errors`;
        }
        message += `\n\nPlease fix the above errors and try uploading the CSV again.`;
        setModifyResult(message);
        setIsModifying(false);
        return;
      }

      const result = await updateCsv(modifyCsvFile) as any;
      
      // Handle both single response and aggregated chunked responses
      const totalUpdated = result.totalUpdated || result.updated || 0;
      const totalTagsCreated = result.totalTagsCreated || result.tagsCreated || 0;
      const totalTagRelationships = result.totalTagRelationships || result.tagRelationships || 0;
      const totalSkipped = result.totalSkipped || result.skipped || 0;
      const allErrors = result.errors || [];
      
      let message = `Success: Updated ${totalUpdated} subscribers`;
      if (totalTagsCreated > 0) {
        message += `\nTags created: ${totalTagsCreated}`;
      }
      if (totalTagRelationships > 0) {
        message += `\nTag relationships: ${totalTagRelationships}`;
      }
      if (totalSkipped > 0) {
        message += `\nSkipped: ${totalSkipped} subscribers`;
      }
      if (allErrors && allErrors.length > 0) {
        message += `\nErrors: ${allErrors.length}`;
        message += `\n${allErrors.slice(0, 5).join('\n')}`;
        if (allErrors.length > 5) {
          message += `\n... and ${allErrors.length - 5} more errors`;
        }
      }
      if (result.notFoundEmails && result.notFoundEmails.length > 0) {
        const display = result.notFoundEmails.slice(0, 10);
        const overflow = result.notFoundEmails.length - display.length;
        message += `\n${result.notFoundEmails.length} email(s) not found in database (no changes made for these):\n`;
        message += display.map((e: string) => `- ${e}`).join('\n');
        if (overflow > 0) message += `\n... and ${overflow} more`;
      }
      setModifyResult(message);
      setModifyCsvFile(null);
      const fileInput = document.getElementById('modifyCsvFileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      if (hasFiltered) applyFilters();
    } catch (error: any) {
      setModifyResult(`Error: ${error.message || 'Update failed'}`);
    } finally {
      setIsModifying(false);
    }
  };

  const handleCloseModifyCsvModal = () => {
    setShowModifyCsv(false);
    setModifyCsvFile(null);
    setModifyResult(null);
    const fileInput = document.getElementById('modifyCsvFileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="m-3">
      <div className="alert alert-info mb-3" role="alert">
        <strong>📧 Email Validation:</strong> Every new subscriber added to the system via API or CSV upload is automatically scanned through our debounce checker to ensure email validity and deliverability.
      </div>
      <Card className="mb-3">
        <Card.Header style={{ backgroundColor: '#f0f4c3' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span>Filter Subscribers</span>
            <div>
              <Button variant="secondary" onClick={() => setShowModifyCsv(true)} className="me-2">
                Modify Subscribers
              </Button>
              <Button variant="primary" onClick={() => setShowCsvUpload(true)}>
                Add Subscribers
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Form>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">
                {getStatusText(showAllTags, timeFilter)}
              </span>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setShowAllTags(!showAllTags)}
              >
                {getToggleButtonText(showAllTags, timeFilter)}
              </Button>
            </div>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Control type="email" placeholder="Email" name="email" onChange={handleFilterChange} value={filters.email} />
              </Col>
              <Col md={4}>
                <Select
                  isMulti
                  name="source"
                  options={sourceOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  onChange={handleSourceChange}
                  placeholder="Select sources..."
                />
              </Col>
              <Col md={4}>
                <Select
                  isMulti
                  name="tags"
                  options={tagOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  onChange={handleTagChange}
                  placeholder="Select positive tags..."
                  inputId="positive-tags-select"
                />
              </Col>
              <Col md={4}>
                <Select
                  isMulti
                  name="negativeTags"
                  options={tagOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  onChange={handleNegativeTagChange}
                  placeholder="Select negative tags..."
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4} className="pt-4 mt-1">
                <Select
                  isMulti
                  name="countries"
                  options={countryOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  onChange={handleCountryChange}
                  placeholder="Select countries..."
                />
              </Col>
              <Col md={4}>
                <Form.Label>Topic:</Form.Label>
                <Select
                  name="topic"
                  options={topicOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={topicOptions.find((o: any) => o.value === (filters.topic_id || defaultTopicId)?.toString())}
                  onChange={(selected: any) => setFilters({ ...filters, topic_id: selected?.value ? parseInt(selected.value) : defaultTopicId })}
                  placeholder="Select topic..."
                  isClearable={false}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              
              <Col md={2}>
                <Form.Label>From:</Form.Label>
                <Form.Control type="date" name="startDate" onChange={handleFilterChange} value={filters.startDate} />
              </Col>
              <Col md={2}>
                <Form.Label>To:</Form.Label>
                <Form.Control type="date" name="endDate" onChange={handleFilterChange} value={filters.endDate} />
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Form.Check type="checkbox" label="Opt-In" name="optIn" onChange={handleFilterChange} defaultChecked />
              </Col>
              <Col md={2} className="d-flex align-items-end">
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={2}>
                <Form.Label>Last Opened From:</Form.Label>
                <Form.Control type="date" name="lastOpenedStartDate" onChange={handleFilterChange} value={filters.lastOpenedStartDate} />
              </Col>
              <Col md={2}>
                <Form.Label>Last Opened To:</Form.Label>
                <Form.Control type="date" name="lastOpenedEndDate" onChange={handleFilterChange} value={filters.lastOpenedEndDate} />
              </Col>
            </Row>
            <Row>
              <Col className="d-flex justify-content-end">
                <Button onClick={applyFilters} disabled={isFiltering}>
                  {isFiltering ? <Spinner animation="border" size="sm" /> : "Filter"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {hasFiltered && (
        <Card className="mb-3">
          <Card.Header as="h5" style={{ backgroundColor: totalSubscribers > 0 ? '#e8f5e9' : '#ffebee' }}>
            Filter Results
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className={`mb-1 ${totalSubscribers > 0 ? 'text-primary' : 'text-muted'}`}>{totalSubscribers.toLocaleString()}</h3>
                <p className="mb-0 text-muted">{totalSubscribers === 0 ? 'No subscribers found with current filters' : 'subscribers found'}</p>
              </div>
              <div>
                {totalSubscribers > 0 && (
                  <>
                    <Button variant="info" onClick={loadDetails} disabled={isFiltering} className="me-2">
                      {isFiltering ? <Spinner animation="border" size="sm" /> : "View Details"}
                    </Button>
                    <Button onClick={handleSummaryClick} disabled={isFetchingSummary} variant="outline-info" className="me-2">
                      {isFetchingSummary ? <Spinner animation="border" size="sm" /> : "Summary"}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteSubscribers} disabled={isDeleting} className="me-2">
                      {isDeleting ? <Spinner animation="border" size="sm" /> : "Delete Subscribers"}
                    </Button>
                    <Button variant="success" onClick={handleDownloadSubscribers} disabled={isDownloading} className="me-2">
                      {isDownloading ? <Spinner animation="border" size="sm" /> : "Download Subscribers"}
                    </Button>
                    {filters.optIn !== 'false' && (
                      <Button variant="warning" onClick={handleOptOutSubscribers} disabled={isOptingOut}>
                        {isOptingOut ? <Spinner animation="border" size="sm" /> : "Opt Out"}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {showDetails && (
        <Card>
          <Card.Header as="h5" style={{ backgroundColor: '#ffe0b2' }}>
            Subscriber Details
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Entry Date</th>
                  <th>Last Opened</th>
                  <th>Source</th>
                  <th>Opt-In</th>
                  {filters.optIn === 'false' && <th>Unsubscribe Reason</th>}
                  <th>IP Address</th>
                  <th>Country</th>
                  {filters.tags.length > 0 && <th>Tags</th>}
                </tr>
              </thead>
              <tbody>
                {subscribers.map((subscriber: any) => (
                  <tr key={subscriber.id}>
                    <td>{subscriber.email}</td>
                    <td>{subscriber.name || 'N/A'}</td>
                    <td>{subscriber.phone || 'N/A'}</td>
                    <td>{formatDate(subscriber.entryDate)}</td>
                    <td>{subscriber.lastOpened ? formatDate(subscriber.lastOpened) : 'N/A'}</td>
                    <td>{subscriber.source}</td>
                    <td>{subscriber.optIn ? 'Yes' : 'No'}</td>
                    {filters.optIn === 'false' && (
                      <td>{subscriber.unsubscribeReason ? REASON_LABELS[subscriber.unsubscribeReason as keyof typeof REASON_LABELS] || subscriber.unsubscribeReason : 'N/A'}</td>
                    )}
                    <td>{subscriber.ipAddress || 'N/A'}</td>
                    <td>{subscriber.countryCode || 'N/A'}</td>
                    {filters.tags.length > 0 && (
                      <td>
                        {subscriber.tags ? (
                          <div className="d-flex flex-wrap gap-1">
                            {subscriber.tags.split(',').map((tag: string, index: number) => (
                              <span key={index} className="badge bg-secondary text-white" style={{fontSize: '0.75rem'}}>
                                {tag.trim()}
                              </span>
                            ))}
                          </div>
                        ) : 'N/A'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination size="sm" className="mt-3">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} />
              <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
            </Pagination>
            <div className="text-muted mt-2">
              Showing {((page - 1) * 20) + 1} - {Math.min(page * 20, totalSubscribers)} of {totalSubscribers} subscribers
            </div>
          </Card.Body>
        </Card>
      )}

      {totalSubscribers > 0 && (
      <Row className="mb-3">
        <Col md={4} style={{ display: 'none'}}>
          <Card>
            <Card.Header style={{ backgroundColor: '#e0f7fa' }}>Assign Tag To Filtered Subscribers</Card.Header>
            <Card.Body>
              <small className="text-muted mb-2 d-block">Note: Only user-created tags are shown (excludes campaign and system-generated tags)</small>
              <Form>
                <Row className="g-2 align-items-center">
                  <Col xs={8}>
                    <Select
                      name="campaignTagToAssign"
                      options={assignableTagOptions}
                      className="basic-single"
                      classNamePrefix="select"
                      onChange={(selectedOption: any) => setCampaignTagToAssign(selectedOption ? selectedOption.value : '')}
                      value={assignableTagOptions.find((option: { value: string; label: string }) => option.value === campaignTagToAssign) || null}
                      placeholder="Select or type a tag..."
                      isClearable
                      inputId="assign-tag-select"
                    />
                  </Col>
                  <Col xs="auto">
                    <Button onClick={handleAssignToCampaign} disabled={!campaignTagToAssign || totalSubscribers === 0 || isAssigningTag}>
                      {isAssigningTag ? <Spinner animation="border" size="sm" /> : "Assign Tag"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header style={{ backgroundColor: '#e6ee9c' }}>Assign Multiple Tags To Filtered Subscribers</Card.Header>
            <Card.Body>
              <small className="text-muted mb-2 d-block">Note: This will distribute subscribers evenly across the selected tags - each tag will be assigned to a different subset of subscribers. Only user-created tags are shown (excludes campaign and system-generated tags).</small>
              <Form>
                <Row className="g-2 align-items-center">
                  <Col xs={8}>
                    <Select
                      isMulti
                      name="campaignTagsToAssign"
                      options={assignableTagOptions}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={(selectedOptions: any) => setCampaignTagsToAssign(selectedOptions ? selectedOptions.map((option: any) => option.value) : [])}
                      value={campaignTagsToAssign.map(tag => ({ value: tag, label: tag }))}
                      placeholder="Select multiple tags..."
                      isClearable
                    />
                  </Col>
                  <Col xs="auto">
                    <Button onClick={handleAssignMultipleTagsToCampaign} disabled={campaignTagsToAssign.length === 0 || totalSubscribers === 0 || isAssigningMultipleTags}>
                      {isAssigningMultipleTags ? <Spinner animation="border" size="sm" /> : "Assign Tags"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} style={{ display: 'none'}}>
          <Card>
            <Card.Header style={{ backgroundColor: '#e8f5e9' }}>Remove Tag From Filtered Subscribers</Card.Header>
            <Card.Body>
              <small className="text-muted mb-2 d-block">Note: Only user-created tags are shown (excludes campaign and system-generated tags)</small>
              <Form>
                <Row className="g-2 align-items-center">
                  <Col xs={8}>
                    <Select
                      name="campaignTagToRemove"
                      options={assignableTagOptions}
                      className="basic-single"
                      classNamePrefix="select"
                      onChange={(selectedOption: any) => setCampaignTagToRemove(selectedOption ? selectedOption.value : '')}
                      value={assignableTagOptions.find((option: { value: string; label: string }) => option.value === campaignTagToRemove) || null}
                      placeholder="Select or type a tag..."
                      isClearable
                    />
                  </Col>
                  <Col xs="auto">
                    <Button onClick={handleRemoveTagFromCampaign} disabled={!campaignTagToRemove || totalSubscribers === 0 || isRemovingTag}>
                      {isRemovingTag ? <Spinner animation="border" size="sm" /> : "Remove Tag"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header style={{ backgroundColor: '#ffcdd2' }}>Remove Multiple Tags From Filtered Subscribers</Card.Header>
            <Card.Body>
              <small className="text-muted mb-2 d-block">Note: This will remove all selected tags from all filtered subscribers who currently have them. Only user-created tags are shown (excludes campaign and system-generated tags).</small>
              <Form>
                <Row className="g-2 align-items-center">
                  <Col xs={8}>
                    <Select
                      isMulti
                      name="campaignTagsToRemove"
                      options={assignableTagOptions}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={(selectedOptions: any) => setCampaignTagsToRemove(selectedOptions ? selectedOptions.map((option: any) => option.value) : [])}
                      value={campaignTagsToRemove.map(tag => ({ value: tag, label: tag }))}
                      placeholder="Select multiple tags..."
                      isClearable
                    />
                  </Col>
                  <Col xs="auto">
                    <Button onClick={handleRemoveMultipleTagsFromCampaign} disabled={campaignTagsToRemove.length === 0 || totalSubscribers === 0 || isRemovingMultipleTags}>
                      {isRemovingMultipleTags ? <Spinner animation="border" size="sm" /> : "Remove Tags"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      )}

      {showSummary && summaryData && (
        <Card className="mb-3">
          <Card.Header style={{ backgroundColor: '#dcedc8' }}>Summary Report</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>Total Subscribers</Card.Title>
                    <Card.Text>
                      <h3>{summaryData.totalSubscribers}</h3>
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>Subscribers by Last Opened Status</Card.Title>
                    <Card.Text>
                      <p><strong>Opened:</strong> {summaryData.openedCount}</p>
                      <p><strong>Not Opened:</strong> {summaryData.notOpenedCount}</p>
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>Subscribers by Source</Card.Title>
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Source</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(summaryData.sourceCounts).map(([source, count]) => (
                          <tr key={source}>
                            <td>{source}</td>
                            <td>{Number(count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title>Subscribers by Country (Top 10)</Card.Title>
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Country</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(summaryData.countryCounts).map(([country, count]) => (
                          <tr key={country}>
                            <td>{country}</td>
                            <td>{Number(count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

          </Card.Body>
        </Card>
      )}

      {/* CSV Upload Modal */}
      <Modal show={showCsvUpload} onHide={handleCloseCsvModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Subscribers via CSV Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={12}>
              <p>Upload a CSV file to bulk import subscribers. The CSV should contain the following columns:</p>
              <ul>
                <li><strong>email</strong> (required) - The subscriber's email address</li>
                <li><strong>name</strong> (optional) - The subscriber's full name</li>
                <li><strong>phone</strong> (optional) - The subscriber's phone number</li>
                <li><strong>source</strong> (required) - The source of the subscriber (e.g., "website", "api")</li>
                <li><strong>optIn</strong> (optional) - true/false for opt-in status</li>
                <li><strong>ipAddress</strong> (optional) - The subscriber's IP address</li>
                <li><strong>countryCode</strong> (optional) - The subscriber's country code</li>
                <li><strong>stateCode</strong> (optional) - The subscriber's state/province code (e.g., "CA", "NY")</li>
                <li><strong>tags</strong> (optional) - Tags separated by dashes (e.g., "newsletter-premium-vip")<br/>
                    <small className="text-muted">Note: Campaign tags and system-generated tags (containing colons) will be automatically excluded</small></li>
                <li><strong>topics</strong> (optional) - Topic name(s) to assign, separated by dashes (e.g., "General-Updates-Promotions"). If empty, subscriber will be assigned to ALL existing topics.</li>
                <li><strong>entryDate</strong> (optional) - Entry date in YYYY-MM-DD or YYYY-MM-DD HH:MM:SS format</li>
                <li><strong>lastOpened</strong> (optional) - Last opened date in YYYY-MM-DD or YYYY-MM-DD HH:MM:SS format</li>
              </ul>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={12}>
              <Button variant="outline-secondary" onClick={handleDownloadSample} className="mb-3">
                Download Sample CSV
              </Button>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Select CSV File</Form.Label>
                <Form.Control
                  id="csvFileInput"
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Check
                type="checkbox"
                id="skipDebounceCheck"
                label="Skip debounce validation"
                checked={skipDebounce}
                onChange={(e: any) => setSkipDebounce(e.target.checked)}
              />
              <small className="text-muted">When enabled, imported subscribers will not be checked against the debounce service</small>
            </Col>
          </Row>

          {csvFile && (
            <Row className="mb-3">
              <Col md={12}>
                <p><strong>Selected file:</strong> {csvFile.name}</p>
              </Col>
            </Row>
          )}

          {uploadResult && (
            <Row className="mb-3">
              <Col md={12}>
                <div className={`alert ${uploadResult.includes('Imported') ? 'alert-info' : 'alert-danger'}`} style={{ whiteSpace: 'pre-line' }}>
                  {uploadResult}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCsvModal}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCsvUpload} 
            disabled={!csvFile || isUploading}
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Upload CSV'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modify Subscribers Modal */}
      <Modal show={showModifyCsv} onHide={handleCloseModifyCsvModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Modify Subscribers via CSV Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={12}>
              <p>Upload a CSV file to bulk update existing subscribers. The CSV should contain the following columns:</p>
              <p className="text-danger"><strong>Note:</strong> Only existing subscribers will be updated. If an email is not found in the database, it will be skipped and no new subscriber will be created.</p>
              <p className="text-info"><strong>Important:</strong> The <strong>email</strong> field is mandatory in the CSV. Other fields will only be updated if a value is provided in that column.</p>
              <ul>
                <li><strong>email</strong> (required) - The subscriber's email address to identify the record</li>
                <li><strong>name</strong> (optional) - Update subscriber's name</li>
                <li><strong>phone</strong> (optional) - Update phone number</li>
                <li><strong>source</strong> (optional) - Update source</li>
                <li><strong>optIn</strong> (optional) - Update opt-in status (true/false) - useful for bulk opt-outs</li>
                <li><strong>ipAddress</strong> (optional) - Update IP address</li>
                <li><strong>countryCode</strong> (optional) - Update country code</li>
                <li><strong>stateCode</strong> (optional) - Update state/province code (e.g., "CA", "NY")</li>
                <li><strong>tags</strong> (optional) - Add tags to existing tags (tags separated by dashes, e.g., "newsletter-premium-vip")<br/>
                    <small className="text-muted">Note: New tags will be added to existing tags, not replace them</small></li>
                <li><strong>topics</strong> (optional) - Topic name(s) to assign, separated by dashes (e.g., "General-Updates-Promotions"). If empty, no topic changes.<br/>
                    <small className="text-muted"><strong>Resubscription:</strong> When optIn=true and topics column is empty, the subscriber will be resubscribed to ALL topics they were previously subscribed to.</small></li>
              </ul>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Button variant="outline-secondary" onClick={handleDownloadModifySample} className="mb-3">
                Download Sample CSV
              </Button>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Select CSV File</Form.Label>
                <Form.Control
                  id="modifyCsvFileInput"
                  type="file"
                  accept=".csv"
                  onChange={handleModifyCsvFileChange}
                />
              </Form.Group>
            </Col>
          </Row>

          {modifyCsvFile && (
            <Row className="mb-3">
              <Col md={12}>
                <p><strong>Selected file:</strong> {modifyCsvFile.name}</p>
              </Col>
            </Row>
          )}

          {modifyResult && (
            <Row className="mb-3">
              <Col md={12}>
                <div className={`alert ${modifyResult.includes('Success') ? 'alert-success' : 'alert-danger'}`} style={{ whiteSpace: 'pre-line' }}>
                  {modifyResult}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModifyCsvModal}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleModifyCsvUpload} 
            disabled={!modifyCsvFile || isModifying}
          >
            {isModifying ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Update Subscribers'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Subscribers;
